import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronDown, ChevronRight, ShoppingCart, User, LogOut } from 'lucide-react';
import Logo from './Logo';
import api from '../lib/api';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [hoverCat, setHoverCat] = useState(null);
  const [hoverSub, setHoverSub] = useState(null);
  const [catsOpen, setCatsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { customer, logout } = useCustomerAuth();

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const topCats = categories.filter((c) => !c.parent_id);
  const childrenOf = (id) => categories.filter((c) => c.parent_id === id);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/noleggio?search=${encodeURIComponent(search.trim())}`);
      setSearchOpen(false);
      setSearch('');
    }
  };

  const navItems = [
    { to: '/', label: 'HOME' },
    { to: '/noleggio', label: 'NOLEGGIO', hasDropdown: true },
    { to: '/contatti', label: 'CONTATTI' },
    {
      label: 'INFORMAZIONI',
      hasInfoDropdown: true,
      children: [
        { to: '/orario', label: 'Orario apertura' },
        { to: '/dati-fiscali', label: 'Dati fiscali' },
        { to: '/pagamenti', label: 'Pagamenti' },
      ],
    },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-200">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-44">
          <Link to="/" className="flex-shrink-0">
            <Logo size="md" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-4 ml-auto mr-4">
            {navItems.map((item) => (
              <div
                key={item.to || item.label}
                className="relative"
                onMouseEnter={() => {
                  if (item.hasDropdown) setCatsOpen(true);
                  if (item.hasInfoDropdown) setInfoOpen(true);
                }}
                onMouseLeave={() => {
                  if (item.hasDropdown) { setCatsOpen(false); setHoverCat(null); setHoverSub(null); }
                  if (item.hasInfoDropdown) setInfoOpen(false);
                }}
              >
                {item.hasInfoDropdown ? (
                  <button
                    type="button"
                    className="nav-link text-[13px] font-bold tracking-tight text-neutral-700 hover:text-[#d62828] flex items-center gap-1"
                  >
                    {item.label}
                    <ChevronDown size={14} />
                  </button>
                ) : (
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `nav-link text-[13px] font-bold tracking-tight text-neutral-700 hover:text-[#d62828] flex items-center gap-1 ${
                        isActive ? 'active text-[#d62828]' : ''
                      }`
                    }
                  >
                    {item.label}
                    {item.hasDropdown && <ChevronDown size={14} />}
                  </NavLink>
                )}
                {item.hasInfoDropdown && infoOpen && (
                  <div className="absolute top-full right-0 pt-2 z-40">
                    <div className="w-56 bg-white shadow-xl border border-neutral-200 rounded-md overflow-hidden">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={() => setInfoOpen(false)}
                          className={({ isActive }) =>
                            `block px-4 py-3 text-sm font-semibold border-b border-neutral-100 last:border-b-0 ${
                              isActive ? 'bg-neutral-50 text-[#d62828]' : 'text-neutral-700 hover:bg-neutral-50 hover:text-[#d62828]'
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
                {item.hasDropdown && catsOpen && (
                  <div className="absolute top-full left-0 pt-2 z-40">
                    <div className="flex bg-white shadow-xl border border-neutral-200 rounded-md overflow-hidden">
                      {/* Level 1 */}
                      <div className="w-56 border-r border-neutral-100">
                        {topCats.map((c) => (
                          <Link
                            key={c.id}
                            to={`/categoria/${c.slug}`}
                            onClick={() => setCatsOpen(false)}
                            onMouseEnter={() => { setHoverCat(c.id); setHoverSub(null); }}
                            className={`flex items-center justify-between px-4 py-3 text-sm font-bold border-b border-neutral-100 last:border-b-0 ${
                              hoverCat === c.id ? 'bg-neutral-50 text-[#d62828]' : 'text-neutral-700 hover:bg-neutral-50 hover:text-[#d62828]'
                            }`}
                          >
                            <span>{c.name}</span>
                            {childrenOf(c.id).length > 0 && <ChevronRight size={14} />}
                          </Link>
                        ))}
                      </div>
                      {/* Level 2 */}
                      {hoverCat && childrenOf(hoverCat).length > 0 && (
                        <div className="w-64 border-r border-neutral-100">
                          {childrenOf(hoverCat).map((s) => (
                            <Link
                              key={s.id}
                              to={`/categoria/${s.slug}`}
                              onClick={() => setCatsOpen(false)}
                              onMouseEnter={() => setHoverSub(s.id)}
                              className={`flex items-center justify-between px-4 py-3 text-sm border-b border-neutral-100 last:border-b-0 ${
                                hoverSub === s.id ? 'bg-neutral-50 text-[#d62828]' : 'text-neutral-700 hover:bg-neutral-50 hover:text-[#d62828]'
                              }`}
                            >
                              <span>{s.name}</span>
                              {childrenOf(s.id).length > 0 && <ChevronRight size={14} />}
                            </Link>
                          ))}
                        </div>
                      )}
                      {/* Level 3 */}
                      {hoverSub && childrenOf(hoverSub).length > 0 && (
                        <div className="w-64">
                          {childrenOf(hoverSub).map((ss) => (
                            <Link
                              key={ss.id}
                              to={`/categoria/${ss.slug}`}
                              onClick={() => setCatsOpen(false)}
                              className="block px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-[#d62828] border-b border-neutral-100 last:border-b-0"
                            >
                              {ss.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-2">
            <button onClick={() => setSearchOpen((v) => !v)} className="p-2 text-neutral-700 hover:text-[#d62828]" aria-label="Cerca">
              <Search size={20} />
            </button>

            <Link to="/carrello" className="relative p-2 text-neutral-700 hover:text-[#d62828]" aria-label="Carrello">
              <ShoppingCart size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#d62828] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Customer area */}
            <div className="relative" onMouseLeave={() => setUserMenu(false)}>
              {customer ? (
                <button onClick={() => setUserMenu((v) => !v)} className="hidden md:inline-flex items-center gap-1 p-2 text-neutral-700 hover:text-[#d62828]">
                  <User size={20} />
                  <span className="text-xs font-bold uppercase">{customer.name?.split(' ')[0] || 'Account'}</span>
                </button>
              ) : (
                <Link to="/cliente/login" className="hidden md:inline-flex items-center gap-1 p-2 text-neutral-700 hover:text-[#d62828]" aria-label="Accedi">
                  <User size={20} />
                </Link>
              )}
              {customer && userMenu && (
                <div className="absolute top-full right-0 mt-1 w-56 bg-white shadow-xl border border-neutral-200 rounded-md overflow-hidden z-40">
                  <div className="px-4 py-2 text-xs text-neutral-500 border-b">{customer.email}</div>
                  <Link to="/cliente/profilo" onClick={() => setUserMenu(false)} className="block px-4 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2"><User size={14} /> Il mio account</Link>
                  <Link to="/carrello" onClick={() => setUserMenu(false)} className="block px-4 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2"><ShoppingCart size={14} /> Carrello ({itemCount})</Link>
                  <button onClick={() => { logout(); setUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2 text-red-600 border-t">
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              )}
            </div>

            <Link to="/admin" className="hidden md:inline-block text-[11px] font-bold tracking-wider text-neutral-500 hover:text-[#d62828] px-3 py-2 border border-neutral-300 rounded ml-1">
              ADMIN
            </Link>
            <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 text-neutral-700" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Search bar */}
        {searchOpen && (
          <form onSubmit={handleSearch} className="py-3 border-t border-neutral-200 fade-in">
            <div className="flex gap-2">
              <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca prodotto, marca, categoria..." className="flex-1 px-4 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 focus:border-[#d62828]" />
              <button type="submit" className="px-5 py-2 bg-[#d62828] text-white rounded font-semibold hover:bg-[#b41f1f]">Cerca</button>
            </div>
          </form>
        )}

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-neutral-200 py-3 fade-in">
            {navItems.map((item) => {
              if (item.hasInfoDropdown) {
                return (
                  <div key={item.label}>
                    <div className="block px-3 py-2.5 text-sm font-bold tracking-wider text-neutral-500">{item.label}</div>
                    {item.children.map((c) => (
                      <NavLink key={c.to} to={c.to} onClick={() => setOpen(false)} className="block px-6 py-2 text-sm text-neutral-700 hover:bg-neutral-50">
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                );
              }
              return (
                <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-bold tracking-wider text-neutral-700 hover:bg-neutral-50">
                  {item.label}
                </NavLink>
              );
            })}
            <Link to="/carrello" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-bold tracking-wider text-[#d62828]">CARRELLO ({itemCount})</Link>
            {customer ? (
              <button onClick={() => { logout(); setOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm font-bold text-red-600">LOGOUT</button>
            ) : (
              <Link to="/cliente/login" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-bold text-neutral-700">ACCEDI</Link>
            )}
            <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-bold tracking-wider text-[#d62828]">ADMIN</Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
